/* LabUserSession.js */

"use strict";
const _ = require("lodash");
const UserSession = require("./UserSession");

class LabUserSession extends UserSession {
    constructor(logger, userFeedback, router, UserEntity) {
        super(logger, UserEntity);
        this._userFeedback = userFeedback;
        this._router = router;
    }
    /*******************************************/
    /* START OVERRIDDEN HANDLER METHODS */
    /*******************************************/
    _onConnectionFailed() {
        this.emit("ConnectionFailed");
        this._userFeedback
            .displayError({
                error: {
                    errorCode: "Connection Failed",
                    errorMsg: "Failed to connect to the Lab Application Web Socket.",
                },
                notDismissable: true,
            })
            .then(() => {
                this._router.reload();
            });
    }
    _onConnectionLost() {
        this.emit("ConnectionLost");
        this._userFeedback
            .displayError({
                error: {
                    errorCode: "Connection Lost",
                    errorMsg: "Connection to the Lab Application Web Socket has been lost.",
                },
            })
            .then(() => {
                this._router.reload();
            });
    }

    _onConnectionShutdown() {
        this._userFeedback
            .displayError({
                error: {
                    errorCode: "Connection Shutdown",
                    errorMsg: "Lab Application Web Socket has been shutdown.",
                },
            })
            .then(() => {
                this._router.reload();
            });
        this.emit("ConnectionShutdown");
    }

    _onNotAuthenticated() {
        this.emit("NotAuthenticated");
    }

    _onUnAuthenticated(cause) {
        this.emit("UnAuthenticated", cause);
        this._unregisterConnectionListeners();
    }
    _onProjectOrgInfoChanged(data) {
        let now = Date.now();
        let activeOrg = {};
        let authorizedOrgs = {};
        //A couple of times this came back null so we will attempt to keep the current org in those cases
        if (!data.curProjectOrg) {
            data.curProjectOrg = _.get(this._activeOrganization, "name");
        }
        if (data.curProjectOrgs && !_.isEmpty(data.curProjectOrgs)) {
            data.curProjectOrgs.forEach((org) => {
                let id = now + "-" + org;
                if (org === data.curProjectOrg) {
                    activeOrg = {
                        id: id,
                        name: org,
                    };
                }
                authorizedOrgs[id] = {
                    name: org,
                    providerName: "GluwareSystemOrganization",
                };
            });
        }
        let id = now + "-sys";
        let name = "sys";
        //If no authorized orgs or active org set it to "sys"
        if (_.isEmpty(authorizedOrgs) || _.isEmpty(activeOrg)) {
            authorizedOrgs[id] = {
                name: name,
                providerName: "GluwareSystemOrganization",
            };
            activeOrg = {
                id: id,
                name: name,
            };
        }

        this._userEntity
            .setProperty("authorizedOrganizations", authorizedOrgs)
            .setProperty("authorizedProjects", data.projects);

        //Notify the world of org changes...
        let oldActiveOrg = this._activeOrganization;
        this._activeOrganization = activeOrg;
        this.emit("ActiveOrganizationChange", this._activeOrganization, oldActiveOrg);
        this.emit("OrganizationChange", this._activeOrganization, oldActiveOrg);

        //Notify the world of project changes...
        let oldACtiveProj = this._activeProject;
        this._activeProject = data.curProject;
        this.emit("ActiveProjectChanged", this._activeProject, oldACtiveProj);

        this.emit("ProjectOrgInfoChanged", {
            activeProject: this._activeProject,
            activeOrganization: this._activeOrganization,
            authorizedProjects: data.projects,
            authorizedOrganizations: authorizedOrgs,
        });
    }
    /*******************************************/
    /* END OVERRIDDEN HANDLER API METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _connectAndAuthenticate(creds, opts) {
        let defaultHost = _.get(document, "location.host");
        let host = _.get(opts, "host", defaultHost);

        let path = _.get(opts, "path", "labWebClient");
        this._connection = this._ConnectionHandlerFactory.getInstance("LAB", host, null, {
            path: path,
        });

        return (
            Promise.resolve()
                .then(() => {
                    return new Promise((resolve, reject) => {
                        this._connection.once("RunLabApp", (data) => {
                            resolve(data);
                        });
                        //this causes the RunLabApp event to be fired
                        this._connection.connect().catch(reject);
                    });
                })
                .then((data) => {
                    this.setLabAppInfo(data, true);
                })
                .then(() => {
                    return this._connection.authenticate(
                        _.get(creds, "username"),
                        _.get(creds, "password")
                    );
                })
                .then((response) => {
                    this._registerConnectionListeners();
                    this._token = response.token;
                })
                //Get the Active Organizations
                .then(() => {
                    return new Promise((resolve) => {
                        this._connection.once("CurProjectOrg", (data) => {
                            let now = Date.now();
                            //TODO do something better in the future
                            let tenantId = now + "-tenantid";
                            let userId = now + "-userid";
                            this._userEntity = new this._UserEntityDefinition(tenantId, userId);

                            //monkeypatch user to add in project methods
                            Object.defineProperty(this._userEntity, "authorizedProjects", {
                                get: function () {
                                    return this._userEntity.getAuthorizedProjects();
                                },
                            });
                            this._userEntity.getAuthorizedProjects = () => {
                                return this._userEntity.getProperty("authorizedProjects");
                            };

                            this._userEntity
                                .setProperty("userName", "labUserName")
                                .setProperty("first", "Lab")
                                .setProperty("last", "App")
                                .setProperty("email", "labapp@gluware.com")
                                .setProperty("eulaAcceptTime", now);

                            //Update the Proj/Org info
                            this._onProjectOrgInfoChanged(data);
                            //Register for future events
                            this._connection.on(
                                "CurProjectOrg",
                                (this._onProjectOrgInfoChanged =
                                    this._onProjectOrgInfoChanged.bind(this))
                            );

                            resolve(this._userEntity);
                        });
                        //this will cause the curProjectOrg event to be sent back
                        this._connection.sendMessage("initProjectOrg", {});
                    });
                })
                .then(() => {
                    this.emit("Authenticated", this._userEntity);
                    return this._userEntity;
                })
        );
    }
    /*******************************************/
    /* END PRIVATE API METHODS */
    /* START PUBLIC OVERIDDEN API METHODS */
    /*******************************************/
    login(creds, opts) {
        //jshint ignore:line
        if (!this.isConnectedAndAuthenticated()) {
            return this._connectAndAuthenticate(creds, opts);
        } else {
            return Promise.resolve(this._userEntity);
        }
    }
    shutdown() {
        if (this._connection) {
            this._connection.removeListener("CurProjectOrg", this._onProjectOrgInfoChanged);
        }
        super.shutdown();
    }
    changeOrganization(org) {
        this.setActiveOrganization(org);
    }
    setActiveOrganization(org) {
        if (this._connection) {
            //this will cause a "CurProjectOrg" which will fire the change events
            //that way we gaurantee the change has actually been made.
            this._connection.sendMessage("projectOrgChanged", {
                curProject: this._activeProject,
                curProjectOrg: org,
            });
        }
        return this;
    }
    /*******************************************/
    /* END PUBLIC OVERIDDEN API METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    get labAppInfo() {
        return this.getLabAppInfo();
    }
    getLabAppInfo() {
        return this._labAppInfo;
    }
    set labAppInfo(data) {
        return this.setLabAppInfo(data);
    }
    setLabAppInfo(data, silent) {
        let old = this._labAppInfo;
        this._labAppInfo = data;
        if (!silent) {
            this.emit("LabAppInfoChanged", this._labAppInfo, old);
        }
        return this;
    }
    get activeProject() {
        return this.getActiveProject();
    }
    getActiveProject() {
        return this._activeProject;
    }
    set activeProject(ap) {
        return this.setActiveProject(ap);
    }
    setActiveProject(ap) {
        if (this._connection) {
            //this will cause a "CurProjectOrg" which will fire the change events
            //that way we gaurantee the change has actually been made.
            this._connection.sendMessage("projectChanged", {
                curProject: ap,
            });
        }
        return this;
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = LabUserSession;
